import React, {useEffect} from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, Grid, TextField } from '@material-ui/core'
import { useDispatch, useSelector } from "react-redux";
import { loadFlights, changeStartDate, changeEndDate, changeFlightStatus } from '../../store/flights';
import RowItem from './RowItem';
import { SelectWithLabel } from '../../utils/selectWithLabel'
import _ from 'lodash'

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: 'rgb(117,117,117,1)',
    color: theme.palette.common.black,
  },
  body: {
    fontSize: 14,
  }
}))(TableCell);

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%'
  },  
  container: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  header: {
    backgroundColor: 'rgb(117,117,117,1)',
    borderRadius: '0px'
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: 200,
  },
  buttonCell: {
    width: '7%'
  }
}));

function createData(airline, flightNum, aircraftModel, departure, depDate, arrival, status, key) {
  return {
    airline,
    flightNum,
    aircraftModel,
    departure,
    depDate,
    arrival,
    status,
    key
  };
}

const pad = (num) => {
  return Number(num) < 10 ? '0' + num : num
}

const configureDate = (date, withTime) => {
  let newDate = new Date(date)
  let day = newDate.getDate()
  let month = newDate.getMonth()
  let year = newDate.getFullYear()
  if (withTime) {
    let time = pad(newDate.getHours()) + ':' + pad(newDate.getMinutes())
    return `${pad(month + 1)}/${pad(day)}/${pad(year)} ${time}`
  }
  else {
    return `${pad(month + 1)}/${pad(day)}/${pad(year)}`
  }
}

// NOTE // getting an extra two days because we are working off local time (while amelia is not)
const getNextDay = (date, type) => {
  let day = Number(date.slice(8, 10))
  let month = Number(date.slice(5, 7))
  let year = Number(date.slice(0, 4))
  let daysInMonth = new Date(year, month, 0).getDate()
  // NOTE // we need to grab the day before or after the selected day
  if (type == 'end') {
    // NOTE // if the selected day is not the end of the month
    if (day < daysInMonth) {
      day += 1
    }
    // NOTE // the selected day is the end of the month
    else {
      // NOTE // if it's not december just add 1 to the month and set the day to the first
      if (month != 12) {
        month += 1
        day = 1
      }
      // NOTE // if it is december set the day and month to 1, and add one to the year
      else {
        year += 1
        month = 1
        day = 1
      }
    }
  }

  else if (type == 'start') {
    if (day != 0) {
      day -= 1
    }
    // NOTE // the selected day is the beggining of the month
    else {
      // NOTE // if it's not january subtract 1 from the month and set the day to the last day of the previous month
      if (month != 12) {
        month -= 1
        day = new Date(year, month, 0).getDate()
      }
      // NOTE // if it is january set the month to december, the day to the 31st, and subtract one from the year
      else {
        year -= 1
        month = 12
        day = 31
      }
    }
  }
  let newDate = year + '-' + pad(month) + '-' + pad(day)
  return newDate
}

export default function Flights() {
  const dispatch = useDispatch();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const flights = useSelector(state => state.entities.flights.list)
  const startDate = useSelector(state => state.entities.flights.startDate);
  const endDate = useSelector(state => state.entities.flights.endDate);
  const flightStatus = useSelector(state => state.entities.flights.flightStatus);
  const classes = useStyles();

  useEffect(() => {
    dispatch(loadFlights(getNextDay(startDate, 'start'), getNextDay(endDate, 'end')));
  }, [startDate, endDate, flightStatus]);
  
  const changeStartDateRq = (event) => {
    dispatch(changeStartDate(event.target.value))
  }

  const changeEndDateRq = (event) => {
    dispatch(changeEndDate(event.target.value))
  }

  const changeFlightStatusRq = (event) => {
    dispatch(changeFlightStatus(event.target.value))
  }

  const paginateFlights = (flights) => { 
    let paginatedFlights = _.cloneDeep(flights)
    let start = page == 0 ? 0 : (page * rowsPerPage)
    paginatedFlights = paginatedFlights.slice(start, (start + rowsPerPage))
    return paginatedFlights
  }

  const filterFlights = () => {
    let flightsCopy = _.cloneDeep(flights)
    let startDay = Number(startDate.slice(8, 10))
    let endDay = Number(endDate.slice(8, 10))
    let filteredFlights = []

    // NOTE // filter flights based on local time
    for (let i = 0; i < flightsCopy.length; i++) {
      const flight = flightsCopy[i];
      let depDay = new Date(flight.departure.scheduledTime).getDate()
      if (depDay >= startDay) {
        if (depDay <= endDay) {
          filteredFlights.push(flight)
        }
      }
    }

    return filteredFlights
  }

  let rows = paginateFlights(filterFlights()).map(
    (flightItem) => {
      return createData(
        flightItem.airlineCode.code,
        flightItem.flightNumber,
        flightItem.aircraftModel.name,
        flightItem.departure.airport.code,
        configureDate(flightItem.departure.scheduledTime, true),
        flightItem.arrival.airport.code,
        flightItem.status.active.toString(),
        flightItem.key
      )
    }) || [];


  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const flightStatusChoices = [
    {label: 'Active', value: false},
    {label: 'All', value: true}
  ]

  return (
    <div>
    <Grid container xs={12} style={{marginBottom: '15px'}}>
      <form className={classes.container} noValidate>
        <TextField
          id="startDate"
          label="Start Date"
          type="date"
          defaultValue={startDate}
          className={classes.textField}
          InputLabelProps={{ shrink: true }}
          onChange={changeStartDateRq}
        />
        <TextField
          id="endDate"
          label="End Date"
          type="date"
          defaultValue={endDate}
          className={classes.textField}
          InputLabelProps={{ shrink: true }}
          onChange={changeEndDateRq}
        />
        {/* defaultVal, handleChange, inputLabel, items */}
        <SelectWithLabel defaultVal={flightStatus} handleChange={changeFlightStatusRq} inputLabel={'Flight Status'} items={flightStatusChoices}/>
      </form>
    </Grid>
    <Paper className={classes.root}>
      <TableContainer >
        <Table aria-label="collapsible table">
          <TableHead className={classes.header}>
            <TableRow >
              <StyledTableCell>Airline</StyledTableCell>
              <StyledTableCell>Flight Number</StyledTableCell>
              <StyledTableCell>Aircraft Model</StyledTableCell>
              <StyledTableCell>Departure</StyledTableCell>
              <StyledTableCell>Departure Date</StyledTableCell>
              <StyledTableCell>Arrival</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <RowItem key={row.name} row={row} index={index} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
      rowsPerPageOptions={[10, 25, 100]}
      component="div"
      count={filterFlights().length}
      rowsPerPage={rowsPerPage}
      page={page}
      onChangePage={handleChangePage}
      onChangeRowsPerPage={handleChangeRowsPerPage}
    />
  </Paper>
    </div>
  );
}
